import { snackbar } from '@/_core/app-components/use';
import { useSendCommand } from '@/_core/cqrs/use';
import { RealTimeAction } from '@/_core/fluxstore/types';
import { createMoveCardToActivityCommand } from '../../../api/commands/factory/createMoveCard';
import { StorymapState } from '../../types';
import { getCardById, isActivity, isSubtask, isTask } from '../../use';
import { createActivityFromSubtask, createActivityFromTask } from '../../use/mapCards';
import { useStorymapStore } from '../../useModule';
import { SetActiveCardIdByClick } from '../cardSelection/setActiveCardIdByClick';
import { FlashJustDragged } from './flashJustDragged';
import { HandleMoveCardResult } from './handleMoveCardResult';

const { registerReducer, registerMiddleware, dispatch } = useStorymapStore();

export class MoveCardToActivity extends RealTimeAction<StorymapState> {
    public static ACTION_TYPE = 'MoveCardToActivity';

    public constructor(public id: string, public newOrder: number) {
        super(MoveCardToActivity.ACTION_TYPE);
    }
}

const canMoveIntoMiddleware = async (state: StorymapState, action: MoveCardToActivity): Promise<boolean> => {
    const card = getCardById(action.id);

    if (isTask(card)) {
        const hasChild = state.subtasks.some((x) => x.taskId === card.id);

        if (hasChild) {
            snackbar.showWarning("Can't move there because it has child cards");
            return false;
        }
    }

    return true;
};

const middleware = async (state: StorymapState, action: MoveCardToActivity): Promise<boolean> => {
    (async () => {
        const cmd = await createMoveCardToActivityCommand(action);
        useSendCommand(action, {
            createCommand: () => cmd,
            onSuccess: (res) => {
                if (res.PushedActivity != null || res.PushedTask != null || (res.UnlinkedCardIds != null && res.UnlinkedCardIds.length > 0)) {
                    dispatch(new HandleMoveCardResult(res));
                }
            },
        });
    })();

    return true;
};

const reducer = (state: StorymapState, action: MoveCardToActivity): void => {
    const card = getCardById(action.id);

    if (isActivity(card)) {
        state.activities.filter((x) => x.order > card.order).forEach((x) => x.order--);

        state.activities.filter((x) => x.order >= action.newOrder).forEach((x) => x.order++);

        card.lastUpdatedAt = new Date();
        card.order = action.newOrder;
    }

    if (isTask(card)) {
        state.tasks.filter((x) => x.activityId === card.activityId && x.order > card.order).forEach((x) => x.order--);
        state.tasks = state.tasks.filter((x) => x.id !== card.id);

        state.activities.filter((x) => x.order >= action.newOrder).forEach((x) => x.order++);

        const activity = createActivityFromTask(card, action.newOrder, state.taskColor, state.activityColor);
        activity.lastUpdatedAt = new Date();
        state.activities.push(activity);
    }

    if (isSubtask(card)) {
        state.subtasks
            .filter((x) => x.taskId == card.taskId && x.releaseId == card.releaseId && x.order > card.order)
            .forEach((x) => x.order--);
        state.subtasks = state.subtasks.filter((x) => x.id !== card.id);

        state.activities.filter((x) => x.order >= action.newOrder).forEach((x) => x.order++);

        const activity = createActivityFromSubtask(card, action.newOrder, state.subtaskColor, state.activityColor);
        activity.lastUpdatedAt = new Date();
        state.activities.push(activity);
    }

    if (!action.isDispatchedByRealTimeCallback) {
        dispatch(new SetActiveCardIdByClick(card.id));
        dispatch(new FlashJustDragged());
    }
};

//@ts-ignore
registerMiddleware(MoveCardToActivity.ACTION_TYPE, canMoveIntoMiddleware);

//@ts-ignore
registerMiddleware(MoveCardToActivity.ACTION_TYPE, middleware);

//@ts-ignore
registerReducer(MoveCardToActivity.ACTION_TYPE, reducer);
